import { canvasFont } from "./canvasType.ts";
import { COLORS } from "../core/constants.ts";

export type DamageVariant = "normal" | "crit" | "synergy" | "score";

export interface FloatingNumber {
  x: number;
  y: number;
  text: string;
  color: string;
  age: number;
  lifetime: number;
  variant: DamageVariant;
  scale: number;
}

const FLOAT_SPEED = 30;
const DEFAULT_LIFETIME = 800;
const CRIT_LIFETIME = 1200;
// BL-208 (S189) — the kill "+N" score pop lingers a touch longer than a plain
// damage number so it reads as a reward, but stays shorter than a crit.
const SCORE_LIFETIME = 950;

export class FloatingDamageNumbers {
  private numbers: FloatingNumber[] = [];

  spawn(x: number, y: number, amount: number, color = "#FFFFFF"): void {
    this.spawnVariant(x, y, amount, "normal", color);
  }

  spawnCrit(x: number, y: number, amount: number): void {
    this.spawnVariant(x, y, amount, "crit", COLORS.AMBER_WARN);
  }

  spawnSynergy(x: number, y: number, amount: number): void {
    this.spawnVariant(x, y, amount, "synergy", COLORS.ICE_CYAN);
  }

  /** BL-208 (S189) — the on-board "+N" score pop at a kill's world position
   *  (the kill-juice loop). Reuses the float funnel with a reward sign + the
   *  gold score hue (COLORS.AMBER_WARN) so a kill reads as points GAINED, not
   *  damage dealt. Render-only; no sim/RNG touch. */
  spawnScore(x: number, y: number, amount: number): void {
    this.spawnVariant(x, y, amount, "score", COLORS.AMBER_WARN);
  }

  private static readonly MAX_NUMBERS = 100;

  private spawnVariant(
    x: number,
    y: number,
    amount: number,
    variant: DamageVariant,
    color: string,
  ): void {
    if (this.numbers.length >= FloatingDamageNumbers.MAX_NUMBERS) return;
    const spread = (Math.random() - 0.5) * 10;
    const prefix = variant === "crit" ? "⚡" : "";
    // BL-208 (S189) — the score pop reads as a REWARD ("+N"); every other
    // variant reads as damage ("-N"). Variant-aware sign so the kill score pop
    // can share this one funnel without re-implementing the float/fade.
    const sign = variant === "score" ? "+" : "-";
    this.numbers.push({
      x: x + spread,
      y,
      text: `${prefix}${sign}${amount}`,
      color,
      age: 0,
      lifetime:
        variant === "crit"
          ? CRIT_LIFETIME
          : variant === "score"
            ? SCORE_LIFETIME
            : DEFAULT_LIFETIME,
      variant,
      scale:
        variant === "crit"
          ? 1.8
          : variant === "synergy"
            ? 1.3
            : variant === "score"
              ? 1.15
              : 1.0,
    });
  }

  update(dt: number): void {
    for (let i = this.numbers.length - 1; i >= 0; i--) {
      const n = this.numbers[i]!;
      n.age += dt;
      n.y -= FLOAT_SPEED * (dt / 1000);
      if (n.age >= n.lifetime) {
        // Swap-remove
        this.numbers[i] = this.numbers[this.numbers.length - 1]!;
        this.numbers.pop();
      }
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    ctx.textAlign = "center";
    for (const n of this.numbers) {
      const progress = n.age / n.lifetime;
      const alpha = 1 - progress;
      // Pop-in: scale overshoot in first 100ms
      const popIn = n.age < 100 ? 1 + (1 - n.age / 100) * 0.4 : 1;
      const finalScale = n.scale * popIn;
      ctx.font = canvasFont("damage", finalScale);
      ctx.globalAlpha = Math.max(0, alpha);
      ctx.fillStyle = n.color;
      ctx.fillText(n.text, n.x, n.y);
    }
    ctx.restore();
  }

  clear(): void {
    this.numbers.length = 0;
  }
}
